import OpenAI from 'openai';
import { RaceParticipant, SeriesParticipant, MatchCandidate } from '../types';

// Configuration for different environments
const AI_CONFIG = {
  // Development: Direct OpenAI API calls
  isDevelopment: !process.env.REACT_APP_AI_API_URL,
  
  // Production: Lambda proxy endpoint (needs /ai prefix and /chat/completions suffix)
  lambdaEndpoint: process.env.REACT_APP_AI_API_URL || 'https://a2m50lpyr4.execute-api.us-west-2.amazonaws.com/prod',
  
  // Development OpenAI client
  openai: process.env.REACT_APP_OPENAI_API_KEY ? new OpenAI({
    apiKey: process.env.REACT_APP_OPENAI_API_KEY,
    dangerouslyAllowBrowser: true // Only for development/prototyping
  }) : null
};

export class AIService {
  // Universal AI completion method that works in both dev and prod
  private static async createCompletion(messages: any[], options: {
    model?: string;
    temperature?: number;
    max_tokens?: number;
  } = {}): Promise<string> {
    // DEBUG: Log environment configuration
    console.log('=== AI SERVICE DEBUG ===');
    console.log('AI_CONFIG.isDevelopment:', AI_CONFIG.isDevelopment);
    console.log('AI_CONFIG.lambdaEndpoint:', AI_CONFIG.lambdaEndpoint);
    console.log('process.env.REACT_APP_AI_API_URL:', process.env.REACT_APP_AI_API_URL);
    console.log('AI_CONFIG.openai available:', !!AI_CONFIG.openai);
    
    const defaultOptions = {
      model: 'gpt-4o',
      temperature: 0.1,
      max_tokens: 4000,
      ...options
    };

    if (AI_CONFIG.isDevelopment) {
      // Development: Use direct OpenAI API
      if (!AI_CONFIG.openai) {
        throw new Error('OpenAI API key not configured for development mode');
      }

      const response = await AI_CONFIG.openai.chat.completions.create({
        ...defaultOptions,
        messages
      });

      const content = response.choices[0]?.message?.content;
      if (!content) {
        throw new Error('No response from OpenAI API');
      }
      return content;

    } else {
      // Production: Use Lambda proxy
      if (!AI_CONFIG.lambdaEndpoint) {
        throw new Error('Lambda AI endpoint not configured for production mode');
      }

      // Construct the full endpoint: base + /ai + /chat/completions
      const fullEndpoint = `${AI_CONFIG.lambdaEndpoint}/ai/chat/completions`;

      console.log('Making fetch request to:', fullEndpoint);
      console.log('Request payload:', JSON.stringify({
        ...defaultOptions,
        messages
      }, null, 2));

      const AuthService = (await import('./authService')).default;
      const token = await AuthService.getInstance().getIdToken();

      const response = await fetch(fullEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          ...defaultOptions,
          messages
        }),
      });

      console.log('Fetch response status:', response.status);
      console.log('Fetch response ok:', response.ok);
      console.log('Fetch response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Response error data:', errorData);
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Response data structure:', {
        hasChoices: !!data.choices,
        choicesLength: data.choices?.length,
        firstChoiceStructure: data.choices?.[0] ? Object.keys(data.choices[0]) : 'no first choice'
      });
      
      const content = data.choices?.[0]?.message?.content;
      if (!content) {
        console.error('No content in response:', data);
        throw new Error('No response from Lambda AI service');
      }
      return content;
    }
  }

  // Helper function to clean AI responses that may contain markdown formatting
  private static cleanJsonResponse(content: string): string {
    // Remove markdown code blocks
    let cleaned = content.replace(/```json\s*/g, '').replace(/```\s*/g, '');
    
    // Remove any leading/trailing whitespace
    cleaned = cleaned.trim();
    
    // If the response starts with explanation text, try to find the JSON part
    const jsonStart = cleaned.indexOf('[') !== -1 ? cleaned.indexOf('[') : cleaned.indexOf('{');
    const jsonEnd = cleaned.lastIndexOf(']') !== -1 ? cleaned.lastIndexOf(']') + 1 : cleaned.lastIndexOf('}') + 1;
    
    if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
      cleaned = cleaned.substring(jsonStart, jsonEnd);
    }
    
    return cleaned;
  }

  static async applyPointsScheme(
    participants: RaceParticipant[], 
    pointsScheme: string
  ): Promise<RaceParticipant[]> {
    console.log('=== NEW CODE-BASED POINTS CALCULATION ===');
    console.log(`Processing ${participants.length} participants with scheme: "${pointsScheme}"`);
    
    try {
      // Get data structure sample for AI to understand the format
      const sampleParticipant = participants[0];
      if (!sampleParticipant) {
        throw new Error('No participants provided for points calculation');
      }

      const dataStructure = {
        totalParticipants: participants.length,
        sampleParticipant: {
          id: "string",
          name: "string", 
          category: "string",
          position: "number",
          license: "string (optional)",
          // Show other fields that exist
          ...Object.keys(sampleParticipant).reduce((acc, key) => {
            if (!['id', 'name', 'category', 'position', 'license'].includes(key)) {
              acc[key] = typeof sampleParticipant[key as keyof RaceParticipant];
            }
            return acc;
          }, {} as Record<string, string>)
        },
        categories: Array.from(new Set(participants.map(p => p.category).filter(Boolean))),
        positionRange: {
          min: Math.min(...participants.map(p => p.position || 999)),
          max: Math.max(...participants.map(p => p.position || 0))
        }
      };

      console.log('Data structure sent to AI:', dataStructure);

      const prompt = `Generate JavaScript code to calculate race points based on this scheme: "${pointsScheme}"

Data Structure:
- Total participants: ${dataStructure.totalParticipants}
- Categories: ${dataStructure.categories.join(', ')}
- Position range: ${dataStructure.positionRange.min} to ${dataStructure.positionRange.max}
- Each participant has: name, category, position, and possibly other fields

Return ONLY a JavaScript function body (just the code between the braces) that:
1. Takes an array called 'participants' as input
2. Returns participants.map() with points added
3. Uses the points scheme: ${pointsScheme}

Example format:
return participants.map(participant => {
  let points = 0;
  if (participant.position === 1) points = 100;
  else if (participant.position === 2) points = 95;
  // ... your logic here based on the scheme
  return { ...participant, points };
});

IMPORTANT: Return ONLY the function body code, no explanations, no markdown, no \`\`\``;

      console.log('Sending prompt to AI...');
      const response = await this.createCompletion([{ role: 'user', content: prompt }], {
        max_tokens: 2000,
        temperature: 0.1
      });

      console.log('AI Response received, length:', response.length);
      console.log('Raw AI response:', response);

      // Clean the response to extract just the function body
      let functionBody = response.trim();
      
      // Remove any markdown formatting
      functionBody = functionBody.replace(/```javascript\n?/g, '').replace(/```\n?/g, '').replace(/```/g, '');
      
      // If it starts with "return", wrap it in braces
      if (functionBody.startsWith('return')) {
        functionBody = `{ ${functionBody} }`;
      }
      
      // Ensure it starts with { and ends with }
      if (!functionBody.startsWith('{')) {
        functionBody = '{' + functionBody;
      }
      if (!functionBody.endsWith('}')) {
        functionBody = functionBody + '}';
      }

      console.log('Cleaned function body:', functionBody);

      // Create the complete function
      const completeFunction = `(function(participants) ${functionBody})`;

      console.log('Complete function to execute:', completeFunction);

      // Execute the function safely
      let calculatePointsFunc: (participants: RaceParticipant[]) => RaceParticipant[];
      try {
        // eslint-disable-next-line no-eval
        calculatePointsFunc = eval(completeFunction);
        console.log('Function compiled successfully');
      } catch (evalError) {
        console.error('Function compilation failed:', evalError);
        console.error('Attempted to compile:', completeFunction);
        throw new Error(`Generated code is invalid: ${evalError instanceof Error ? evalError.message : 'Unknown evaluation error'}`);
      }

      // Test the function with a small sample first
      console.log('Testing function with sample data...');
      const testResult = calculatePointsFunc(participants.slice(0, 1));
      console.log('Test result:', testResult);

      // Apply the function to all participants
      console.log('Applying function to all participants...');
      const result = calculatePointsFunc(participants);

      // Validate the result
      if (!Array.isArray(result) || result.length !== participants.length) {
        throw new Error(`Generated function returned invalid result: expected ${participants.length} participants, got ${result?.length || 'non-array'}`);
      }

      // Ensure all participants have valid points
      const validatedResult = result.map((participant, index) => {
        const points = typeof participant.points === 'number' ? participant.points : 0;
        return {
          ...participants[index], // Use original data as base
          points: points
        };
      });

      console.log('Points calculation completed successfully');
      console.log('Sample results:', validatedResult.slice(0, 3).map(p => ({ name: p.name, position: p.position, points: p.points })));
      
      return validatedResult;

    } catch (error) {
      console.error('=== POINTS CALCULATION ERROR ===');
      console.error('Error details:', error);
      console.error('Stack trace:', error instanceof Error ? error.stack : 'No stack trace');
      
      // Failsafe: Use simple position-based points if AI fails
      console.log('Attempting failsafe basic points calculation...');
      try {
        const failsafeResult = participants.map(participant => {
          let points = 0;
          const position = participant.position || 999;
          
          // Simple declining points scheme
          if (position <= 50) {
            points = Math.max(0, 101 - position);
          }
          
          return {
            ...participant,
            points
          };
        });
        
        console.log('Failsafe calculation completed');
        console.log('Sample failsafe results:', failsafeResult.slice(0, 3).map(p => ({ name: p.name, position: p.position, points: p.points })));
        return failsafeResult;
        
      } catch (failsafeError) {
        console.error('Even failsafe calculation failed:', failsafeError);
        throw new Error(`Failed to apply points scheme: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  }

  static async matchParticipants(
    newParticipants: RaceParticipant[],
    existingParticipants: SeriesParticipant[]
  ): Promise<MatchCandidate[]> {
    try {
      console.log(`Starting category-specific participant matching: ${newParticipants.length} new participants against ${existingParticipants.length} existing participants`);
      
      // Group participants by category for category-specific matching
      const newByCategory = this.groupParticipantsByCategory(newParticipants);
      const existingByCategory = this.groupExistingParticipantsByCategory(existingParticipants);
      
      console.log('New participants by category:', Object.keys(newByCategory));
      console.log('Existing participants by category:', Object.keys(existingByCategory));
      
      const allMatches: MatchCandidate[] = [];
      const crossCategoryWarnings: { participant: RaceParticipant; crossCategoryMatches: string[] }[] = [];
      
      // Process each category separately
      for (const [category, categoryNewParticipants] of Object.entries(newByCategory)) {
        const categoryExistingParticipants = existingByCategory[category] || [];
        
        console.log(`Processing category "${category}": ${categoryNewParticipants.length} new vs ${categoryExistingParticipants.length} existing`);
        
        // Step 1: Exact matches within category
        const { exactMatches, unmatchedNew, unmatchedExisting } = this.findExactMatches(
          categoryNewParticipants, 
          categoryExistingParticipants
        );
        
        console.log(`Category "${category}" exact matches: ${exactMatches.length}`);
        
        // Step 2: AI fuzzy matching within category
        let fuzzyMatches: MatchCandidate[] = [];
        if (unmatchedNew.length > 0 && unmatchedExisting.length > 0) {
          fuzzyMatches = await this.matchParticipantsSingle(unmatchedNew, unmatchedExisting);
        }
        
        console.log(`Category "${category}" fuzzy matches: ${fuzzyMatches.length}`);
        
        // Step 3: Check for cross-category name conflicts
        for (const newParticipant of categoryNewParticipants) {
          const crossCategoryMatches = this.findCrossCategoryNameMatches(
            newParticipant, 
            existingParticipants.filter(p => p.category !== category)
          );
          
          if (crossCategoryMatches.length > 0) {
            crossCategoryWarnings.push({
              participant: newParticipant,
              crossCategoryMatches: crossCategoryMatches.map(match => 
                `${match.name} (${match.category})`
              )
            });
          }
        }
        
        // Step 4: Mark remaining unmatched as "new"
        const newParticipantMatches: MatchCandidate[] = unmatchedNew
          .filter(newP => !fuzzyMatches.some(fm => fm.newParticipant.id === newP.id))
          .map(newP => ({
            newParticipant: newP,
            existingMatches: [],
            status: 'new' as const
          }));
        
        allMatches.push(...exactMatches, ...fuzzyMatches, ...newParticipantMatches);
      }
      
      // Add cross-category warnings to appropriate match candidates
      this.addCrossCategoryWarnings(allMatches, crossCategoryWarnings);
      
      console.log(`Category-specific matching complete: ${allMatches.length} total matches, ${crossCategoryWarnings.length} cross-category warnings`);
      
      return allMatches;
    } catch (error) {
      console.error('Error matching participants:', error);
      throw new Error(`Failed to match participants: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private static groupParticipantsByCategory(participants: RaceParticipant[]): Record<string, RaceParticipant[]> {
    return participants.reduce((groups, participant) => {
      const category = participant.category || 'No Category';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(participant);
      return groups;
    }, {} as Record<string, RaceParticipant[]>);
  }

  private static groupExistingParticipantsByCategory(participants: SeriesParticipant[]): Record<string, SeriesParticipant[]> {
    return participants.reduce((groups, participant) => {
      const category = participant.category || 'No Category';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(participant);
      return groups;
    }, {} as Record<string, SeriesParticipant[]>);
  }

  private static findCrossCategoryNameMatches(
    newParticipant: RaceParticipant, 
    existingParticipantsOtherCategories: SeriesParticipant[]
  ): SeriesParticipant[] {
    const matches: SeriesParticipant[] = [];
    
    for (const existing of existingParticipantsOtherCategories) {
      // Check for exact name match across categories
      if (existing.name.toLowerCase().trim() === newParticipant.name.toLowerCase().trim()) {
        matches.push(existing);
      }
      // Check for exact license match across categories
      else if (
        newParticipant.license && 
        existing.license && 
        existing.license.toLowerCase().trim() === newParticipant.license.toLowerCase().trim()
      ) {
        matches.push(existing);
      }
    }
    
    return matches;
  }

  private static addCrossCategoryWarnings(
    allMatches: MatchCandidate[], 
    crossCategoryWarnings: { participant: RaceParticipant; crossCategoryMatches: string[] }[]
  ): void {
    for (const warning of crossCategoryWarnings) {
      const matchCandidate = allMatches.find(
        match => match.newParticipant.id === warning.participant.id
      );
      
      if (matchCandidate) {
        // Add warning information to the match candidate
        (matchCandidate as any).crossCategoryWarning = {
          message: `⚠️ Same name found in other categories: ${warning.crossCategoryMatches.join(', ')}`,
          otherCategories: warning.crossCategoryMatches
        };
      }
    }
  }

  private static findExactMatches(
    newParticipants: RaceParticipant[],
    existingParticipants: SeriesParticipant[]
  ): {
    exactMatches: MatchCandidate[];
    unmatchedNew: RaceParticipant[];
    unmatchedExisting: SeriesParticipant[];
  } {
    const exactMatches: MatchCandidate[] = [];
    const unmatchedNew: RaceParticipant[] = [];
    const usedExistingIds = new Set<string>();

    for (const newParticipant of newParticipants) {
      let exactMatch: SeriesParticipant | null = null;
      let matchReason: string[] = [];

      // 1. Try exact license number match first (highest priority)
      if (newParticipant.license && newParticipant.license.trim()) {
        const licenseMatch = existingParticipants.find(existing => 
          existing.license && 
          existing.license.toLowerCase().trim() === newParticipant.license!.toLowerCase().trim() &&
          !usedExistingIds.has(existing.id)
        );
        if (licenseMatch) {
          exactMatch = licenseMatch;
          matchReason.push('exact license match');
        }
      }

      // 2. Try exact name match (case-insensitive, but same spelling)
      if (!exactMatch && newParticipant.name && newParticipant.name.trim()) {
        const nameMatch = existingParticipants.find(existing => 
          existing.name.toLowerCase().trim() === newParticipant.name.toLowerCase().trim() &&
          !usedExistingIds.has(existing.id)
        );
        if (nameMatch) {
          exactMatch = nameMatch;
          matchReason.push('exact name match');
        }
      }

      if (exactMatch) {
        exactMatches.push({
          newParticipant,
          existingMatches: [{
            participant: exactMatch,
            confidence: 100,
            reasons: matchReason
          }],
          status: 'auto',
          selectedMatch: exactMatch
        });
        usedExistingIds.add(exactMatch.id);
      } else {
        unmatchedNew.push(newParticipant);
      }
    }

    // Find existing participants that weren't matched
    const unmatchedExisting = existingParticipants.filter(existing => 
      !usedExistingIds.has(existing.id)
    );

    return { exactMatches, unmatchedNew, unmatchedExisting };
  }

  private static async matchParticipantsSingle(
    newParticipants: RaceParticipant[],
    existingParticipants: SeriesParticipant[]
  ): Promise<MatchCandidate[]> {
    try {
      // Simplify new participants data (only essential fields)
      const simplifiedNewParticipants = newParticipants.map(p => ({
        id: p.id,
        name: p.name,
        category: p.category,
        license: p.license
      }));

      // Simplify existing participants data (only essential fields, exclude race history)
      const simplifiedExistingParticipants = existingParticipants.map(p => ({
        id: p.id,
        name: p.name,
        category: p.category,
        license: p.license,
        totalPoints: p.totalPoints
      }));

      const prompt = `
You are a participant matching system for race series. Match new race participants with existing series participants.

New Race Participants:
${JSON.stringify(simplifiedNewParticipants, null, 2)}

Existing Series Participants:
${JSON.stringify(simplifiedExistingParticipants, null, 2)}

CRITICAL RULE: You must NEVER suggest a match based on "same category" alone. Category similarity is NOT a valid reason for matching.

STRICT MATCHING RULES:
1. ONLY suggest matches if there is GENUINE NAME SIMILARITY or EXACT LICENSE MATCH
2. Name similarity examples that ARE acceptable (with appropriate confidence):
   - "John Smith" vs "J. Smith" → 70% (abbreviation)
   - "Michael Johnson" vs "Mike Johnson" → 65% (nickname)  
   - "Sarah Connor" vs "Sara Connor" → 75% (spelling variation)
   - "Robert Williams" vs "Bob Williams" → 65% (common nickname)
   - "Katherine Smith" vs "Kate Smith" → 65% (nickname)
   - "Smith, John" vs "John Smith" → 80% (format difference)
   - "John A. Smith" vs "John Smith" → 75% (middle initial)
3. Name examples that are ABSOLUTELY NOT acceptable matches:
   - "Libbie Young" vs "Izzy Crimaldi" → NEVER MATCH (completely different names)
   - "John Smith" vs "Mary Johnson" → NEVER MATCH (completely different people)
   - "David Wilson" vs "Lisa Brown" → NEVER MATCH (no similarity)
   - "Alex Miller" vs "Chris Davis" → NEVER MATCH (different names entirely)
4. NEVER EVER suggest matches based solely on same category
5. Names must share actual letters/sounds/nicknames to be considered

CONFIDENCE SCORING (be extremely conservative):
- 90-100%: ONLY for exact license number matches (never for names)
- 70-80%: Clear name patterns like format differences or spelling variations
- 60-70%: Nickname variations (Mike/Michael, Bob/Robert, etc.)
- 50-60%: Possible typos or uncertain abbreviations
- Below 50%: DO NOT suggest the match

ABSOLUTELY FORBIDDEN REASONS:
- "same category" (NEVER use this)
- "similar points" (NEVER use this)
- "both are female/male" (NEVER use this)
- "similar age" (NEVER use this)

ONLY VALID REASONS (you must have at least ONE):
- "exact license match" (license numbers are identical) → 95% confidence
- "nickname variation" (Mike/Michael, Bob/Robert, etc.) → 65% confidence
- "name abbreviation" (J. Smith/John Smith) → 70% confidence
- "spelling variation" (Sara/Sarah) → 75% confidence
- "format difference" (Smith, John vs John Smith) → 80% confidence
- "possible typo" (only for very close spellings) → 55% confidence

Common nickname patterns to recognize:
- Michael → Mike, Mick
- Robert → Bob, Rob, Bobby
- William → Bill, Will, Billy
- Richard → Rick, Dick, Rich
- James → Jim, Jimmy
- John → Jack, Johnny
- Elizabeth → Liz, Beth, Betty
- Katherine → Kate, Katie, Kathy
- Christopher → Chris
- Anthony → Tony
- Daniel → Dan, Danny
- Matthew → Matt
- Andrew → Andy
- Jennifer → Jen, Jenny
- Jessica → Jess
- Patricia → Pat, Patty
- Susan → Sue, Susie

CRITICAL: If names are completely different with no license match, mark as "new" participant. Do NOT force matches.

Return ONLY valid JSON with this structure:
[
  {
    "newParticipant": { simplified participant object },
    "existingMatches": [
      {
        "participant": { simplified existing participant object },
        "confidence": 65,
        "reasons": ["nickname variation - Mike/Michael"]
      }
    ],
    "status": "review"
  }
]

Do NOT include markdown formatting. Start with [ and end with ].
`;

      const response = await this.createCompletion([{ role: 'user', content: prompt }]);

      // Log response size for debugging
      console.log(`AI response size: ${response.length} characters`);
      console.log(`Processing ${newParticipants.length} new participants against ${existingParticipants.length} existing participants`);

      // Parse the JSON response
      let simplifiedMatchCandidates;
      try {
        simplifiedMatchCandidates = JSON.parse(this.cleanJsonResponse(response));
      } catch (parseError) {
        console.error('JSON parsing failed.');
        console.error('Response length:', response.length);
        console.error('First 500 chars:', response.substring(0, 500));
        console.error('Last 500 chars:', response.substring(Math.max(0, response.length - 500)));
        console.error('Parse error:', parseError);
        throw new Error(`Invalid JSON response from AI: ${parseError instanceof Error ? parseError.message : 'Unknown parsing error'}`);
      }
      
      // Validate the response
      if (!Array.isArray(simplifiedMatchCandidates)) {
        throw new Error('AI response is not an array');
      }

      // Reconstruct full match candidates by merging simplified AI response with original data
      const fullMatchCandidates: MatchCandidate[] = simplifiedMatchCandidates.map((simplifiedCandidate: any) => {
        // Find the original new participant
        const originalNewParticipant = newParticipants.find(p => p.id === simplifiedCandidate.newParticipant.id);
        if (!originalNewParticipant) {
          throw new Error(`Could not find original new participant with id: ${simplifiedCandidate.newParticipant.id}`);
        }

        // Reconstruct existing matches with full participant data and validate reasons
        const validExistingMatches = simplifiedCandidate.existingMatches
          .filter((match: any) => {
            // Filter out matches with invalid reasons
            const invalidReasons = ['same category', 'similar points', 'both are female', 'both are male', 'similar age'];
            const hasOnlyInvalidReasons = match.reasons.every((reason: string) => 
              invalidReasons.some(invalid => reason.toLowerCase().includes(invalid.toLowerCase()))
            );
            
            if (hasOnlyInvalidReasons) {
              console.warn(`Filtering out invalid match suggestion: ${originalNewParticipant.name} vs ${match.participant.name} with reasons: ${match.reasons.join(', ')}`);
              return false;
            }
            
            return true;
          })
          .map((match: any) => {
            const originalExistingParticipant = existingParticipants.find(p => p.id === match.participant.id);
            if (!originalExistingParticipant) {
              throw new Error(`Could not find original existing participant with id: ${match.participant.id}`);
            }

            return {
              participant: originalExistingParticipant,
              confidence: match.confidence,
              reasons: match.reasons
            };
          });

        // If no valid matches remain, mark as new
        const status = validExistingMatches.length === 0 ? 'new' : simplifiedCandidate.status;

        return {
          newParticipant: originalNewParticipant,
          existingMatches: validExistingMatches,
          status: status
        };
      });

      return fullMatchCandidates;
    } catch (error) {
      console.error('Error matching participants:', error);
      throw new Error(`Failed to match participants: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  static async mapColumns(
    sourceHeaders: string[],
    targetHeaders: string[]
  ): Promise<{ [sourceColumn: string]: string }> {
    try {
      const prompt = `
You are a CSV column mapping system. Map source columns to target columns based on semantic similarity.

Source Headers: ${JSON.stringify(sourceHeaders)}
Target Headers: ${JSON.stringify(targetHeaders)}

Instructions:
1. Map each source header to the most appropriate target header
2. Consider variations like "Name" vs "name", "DOB" vs "birthDate", "License" vs "licenseNumber"
3. Return a mapping object where keys are source headers and values are target headers
4. If no good match exists, map to null

IMPORTANT: Return ONLY a valid JSON object.
Do NOT include any markdown formatting, code blocks, or explanations.
Do NOT wrap the response in \`\`\`json or any other formatting.
Start your response directly with { and end with }.
`;

      const response = await this.createCompletion([{ role: 'user', content: prompt }]);

      try {
        return JSON.parse(this.cleanJsonResponse(response));
      } catch (parseError) {
        console.error('JSON parsing failed. Raw content:', response);
        console.error('Cleaned content:', this.cleanJsonResponse(response));
        throw new Error(`Invalid JSON response from AI: ${parseError instanceof Error ? parseError.message : 'Unknown parsing error'}`);
      }
    } catch (error) {
      console.error('Error mapping columns:', error);
      throw new Error(`Failed to map columns: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  static async detectFieldMapping(headers: string[], sampleData: any[]): Promise<{
    rank: string | null;
    name: string | null;
    category: string | null;
    extraFields: string[];
  }> {
    try {
      const prompt = `
Analyze this CSV data and identify the key fields for race results processing.

Headers: ${JSON.stringify(headers)}
Sample data (first 3 rows): ${JSON.stringify(sampleData.slice(0, 3))}

I need to identify these 3 critical fields:
1. RANK/POSITION field - Contains numeric positions (1, 2, 3, etc.) that may repeat for different categories
2. NAME field - Contains participant names (could be single "name" field or separate first/last name fields)  
3. CATEGORY field - Contains category/division/class names that group participants

Rules:
- Look for numeric sequences starting at 1 that repeat (indicating position/rank within categories)
- Category field will have repeated values that change as groups change
- Name field contains human names
- Everything else should be marked as extra fields

Return ONLY a JSON object with this exact structure:
{
  "rank": "field_name_for_position",
  "name": "field_name_for_name", 
  "category": "field_name_for_category",
  "extraFields": ["list", "of", "other", "field", "names"]
}

IMPORTANT: Return ONLY valid JSON. No markdown, no explanations, no code blocks.`;

      const response = await this.createCompletion([{ role: 'user', content: prompt }]);
      const mapping = JSON.parse(this.cleanJsonResponse(response));
      
      // Validate the mapping
      if (!mapping.rank || !mapping.name || !mapping.category) {
        throw new Error('AI could not identify all required fields');
      }
      
      // Ensure extraFields contains all other fields
      const usedFields = [mapping.rank, mapping.name, mapping.category];
      mapping.extraFields = headers.filter(h => !usedFields.includes(h));
      
      return mapping;
    } catch (error) {
      console.error('AI field mapping failed:', error);
      // Fallback to best-guess mapping
      return {
        rank: headers.find(h => /^(rank|position|place|pos)$/i.test(h)) || headers[0],
        name: headers.find(h => /^(name|participant|rider|driver)$/i.test(h)) || headers[1],
        category: headers.find(h => /^(category|class|division|group)$/i.test(h)) || headers[2],
        extraFields: headers.slice(3)
      };
    }
  }

  static async validateApiKey(): Promise<boolean> {
    try {
      const response = await this.createCompletion([{ role: 'user', content: 'Hello' }], { max_tokens: 5 });
      
      return !!response;
    } catch (error) {
      console.error('API key validation failed:', error);
      return false;
    }
  }
} 